import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const LABEL = { soulai_coach: 'SoulAI Coach', baby_tracker: 'Baby Tracker' };
const STORE = { APP_STORE: 'App Store', PLAY_STORE: 'Play Store' };

export const StoreSubscriptionsCard = ({ userId, isSuperadmin }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/api/iap/admin/ledger/${userId}`).then((r) => setData(r.data)).catch(() => {}), [userId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); } catch (err) { toast.error(err.response?.data?.detailObject?.code || err.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  if (!data) return null;
  const rows = (data.ledger || []).filter((r) => r.store_managed);
  const tester = !!data.iap_sandbox_tester;

  return (
    <div className="mt-3 rounded-xl border bg-muted/20 p-3 space-y-2" data-testid="store-subscriptions-card">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 shrink-0" />Store subs</p>
        {isSuperadmin && (
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={busy} data-testid="store-resync-btn"
            onClick={() => run(() => api.post(`/api/iap/admin/resync/${userId}`), 'Synced from RevenueCat')}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}<span className="ml-1">Resync</span>
          </Button>
        )}
      </div>
      {rows.length === 0 && <p className="text-xs text-muted-foreground" data-testid="store-subscriptions-empty">No App Store / Play purchases yet.</p>}
      {rows.map((r) => (
        <div key={r.id} className="rounded-lg bg-white/60 border border-border/60 px-2.5 py-1.5 text-xs flex items-center gap-2 flex-wrap" data-testid={`store-sub-row-${r.product}`}>
          <span className="font-medium">{LABEL[r.product] || r.product}</span>
          <span className="text-muted-foreground">{r.plan} · {STORE[r.rc?.store] || r.rc?.store || 'store'}</span>
          <Badge variant="outline" className={r.status === 'active' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}>{r.status}</Badge>
          {r.rc?.environment === 'SANDBOX' && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">sandbox</Badge>}
          {r.in_grace && <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200">billing issue</Badge>}
          <span className="ml-auto text-muted-foreground">{r.will_renew ? 'renews' : 'ends'} {fmt(r.expires_at)}</span>
        </div>
      ))}
      {isSuperadmin && (
        <div className="pt-1 border-t border-border/60 space-y-1.5">
          <p className="text-[11px] text-muted-foreground">Sandbox tester: TestFlight test purchases unlock real access{data.rc?.allow_sandbox_grants ? ' (already on for everyone here)' : ''}</p>
          <Button variant={tester ? 'default' : 'outline'} size="sm" className="h-7 text-xs w-full" disabled={busy} data-testid="store-sandbox-tester-toggle"
            onClick={() => run(() => api.post(`/api/iap/admin/sandbox-tester/${userId}`, { enabled: !tester }), tester ? 'Sandbox tester off' : 'Sandbox tester on')}>
            {tester ? 'Sandbox tester: ON' : 'Make sandbox tester'}
          </Button>
        </div>
      )}
    </div>
  );
};
